import {Avatar, Button, Popconfirm} from 'antd'
import {event_edit_Api} from 'apis/event'
import {getId, getType} from 'apis/model/base'
import EmptyHolder from 'components/EmptyHolder'
import {createValue} from 'components/form/utils'
import {useLookupField} from 'components/LookupField'
import getAvatar from 'helpers/getAvatar'
import getLinkToDetail from 'helpers/getLinkToDetail'
import getTitle from 'helpers/getTitle'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useContext, useMemo, useState} from 'react'
import {Link} from 'react-router-dom'
import createEditableForm from 'views/AppLocals/factory/createEditableForm'
import {bindQueryParam} from 'views/AppLocals/functions/routerHelper'
import {NullAsDefault, renderIf} from 'views/Shared'
import EventContext from '../EventContext'
import {FaUserTie} from "react-icons/fa6";
import {eventSchema} from "../../../../apis/schema/event";
import {useSelectEntities} from "../../../../redux/entities/useSelectEntities";
import {BiTrash} from "react-icons/bi";

const formFields = [{name: 'speakers', defaultValue: []}]

const SpeakerItem = ({item, actions}) => {
    const t = useTranslate()
    const {value, speaker} = item ?? {}
    const title = value ?? t('undefined')

    const description = useMemo(() => {
        let content
        if (speaker) {
            content = (
                <div className="flex items-baseline flex-1 gap-1 text-sm leading-snug">
                    {renderIf(
                        item?.patron_saint_name,
                        <span className="font-medium text-color-100 italic">{item?.patron_saint_name}</span>
                    )}
                    <Link
                        target="_blank"
                        to={getLinkToDetail(speaker)}
                        className="font-bold text-color-000 leading-snug no-underline">
                        {title}
                    </Link>
                </div>
            )
        } else {
            content = (
                <div className="flex items-baseline flex-1 gap-1 text-sm leading-snug">
                    {renderIf(
                        item?.patron_saint_name,
                        <span className="font-medium text-color-100 italic">{item?.patron_saint_name}</span>
                    )}
                    <div className="font-bold text-color-000 leading-snug">
                        {title}
                    </div>
                </div>
            )
        }
        return (
            <div className="flex flex-col flex-1">
                {content}
                {renderIf(
                    speaker && !!getType(speaker),
                    <span className="font-medium text-xs text-blue-500 leading-tight italic">
                        {t(getType(speaker))}
                    </span>
                )}
            </div>
        )
    }, [speaker])

    return (
        <div className="w-full p-3 flex items-center gap-2 relative list-item">
            <Avatar
                size={40}
                src={getAvatar(speaker) || item?.cover_photo || item?.thumbnail}
                icon={<FaUserTie size={20} className="text-color-500"/>}
                className="flex flex-center background-200"
            />
            {description}
            {actions}
        </div>
    )
}

export const SpeakersPicker = ({
                                   name,
                                   label,
                                   value,
                                   onChange = NullAsDefault,
                                   renderEmpty = () => <EmptyHolder/>,
                                   renderButton = (child, params) => child,
                                   ...props
                               }) => {
    const t = useTranslate()
    const [speakers, setSpeakers] = useState(Array.from(value ?? []));

    const list = useMemo(() => {
        if (_.isEmpty(speakers)) {
            return renderEmpty()
        }
        return speakers
            .map((e, remapIndex) => ({...e, key: remapIndex}))
            .map((item, index) => (
                <SpeakerItem
                    key={index}
                    item={item}
                    actions={
                        <Popconfirm
                            placement="left"
                            title={
                                <div className="font-medium text-sm text-color-100 italic">
                                    {t('are you sure delete this speaker')}
                                </div>
                            }
                            okText={t('sure')}
                            cancelText={t('cancel')}
                            okButtonProps={{
                                type: 'primary',
                                className:
                                    'button-rounded-xs no-border text-xs font-medium px-3',
                            }}
                            cancelButtonProps={{
                                className:
                                    'button-rounded-xs no-border text-xs font-medium px-3',
                            }}
                            onConfirm={() => {
                                const _speakers = speakers
                                    .map((e, remapIndex) => ({...e, key: remapIndex}))
                                    .filter((el) => el.key !== item.key)
                                setSpeakers(_speakers)
                                onChange(createValue(name, [..._speakers]))
                            }}>
                            <BiTrash
                                size={15}
                                className="text-red-300 hover:text-red-600 cursor-pointer"
                            />
                        </Popconfirm>
                    }
                />
            ))
    }, [value, speakers])

    const {toggle, isToggle, modal} = useLookupField({
        apiPath: '/lookup/user/get-by-types',
        handleChange: (e) => {
            setSpeakers([
                ...speakers,
                {
                    ...e.target,
                    speaker_name: e.target.value,
                },
            ])
            onChange(
                createValue(name, [
                    ...speakers,
                    {
                        ...e.target,
                        speaker_name: e.target.value,
                    },
                ])
            )
        },
        ...props,
    })

    return (
        <div className="flex flex-col gap-4">
            {renderButton(
                <div className="flex items-center justify-between gap-2">
                    <div className="flex flex-col flex-1">
                        <h2 className="font-bold text-xl text-gray-800 tracking-wide">
                            {t('speakers')}
                        </h2>
                        <div className="my-1 w-20 h-1 rounded-full bg-gray-400"/>
                    </div>
                    <Button
                        type="primary"
                        className="no-border button-rounded-lg"
                        onClick={() => {
                            toggle(true)
                        }}>
                        {t('add new')}
                    </Button>
                </div>,
                {
                    toggle,
                }
            )}
            <div className="flex flex-col divide-y divide-gray-200 list-item-workspace">
                {list}
            </div>
            {renderIf(isToggle, modal)}
        </div>
    )
}

const formSchema = [
    {
        children: [
            {
                name: 'speakers',
                component: ({name, value, onChange}) => (
                    <SpeakersPicker
                        name={name}
                        value={value}
                        title="speakers"
                        onChange={onChange}
                        placeholder="select user"
                    />
                ),
            },
        ],
    },
]

const EventSettingSpeakers = () => {
    const {event: _event} = useContext(EventContext)
    const event = useSelectEntities(getId(_event), eventSchema)

    const onPreSubmit = ({speakers, ...values}) =>
        _.omitBy(
            {
                ...values,
                speakers: speakers
                    ? JSON.stringify(
                        Array.from(speakers ?? [])
                            .filter((e) => e.value)
                            .map((speaker) => ({
                                type: getType(speaker),
                                speaker_id: speaker?.id,
                                speaker_name: speaker?.value,
                            }))
                    )
                    : undefined,
            },
            _.isUndefined
        )

    const initialized = useCallback(
        ({speakers, ...values}) => ({
            ...values,
            speakers: Array.from(speakers ?? []).map((item) => ({
                speaker: item?.speaker,
                id: item?.speaker?.id,
                _type: getType(item?.speaker),
                avatar: getAvatar(item?.speaker),
                value: item?.speaker_name ?? getTitle(item?.speaker),
            })),
        }),
        [event.speakers]
    )

    if (_.isEmpty(event)) return null

    return createEditableForm(
        formFields,
        formSchema
    )({
        item: event,
        query: bindQueryParam({
            id: getId(event),
        }),
        onPreSubmit,
        initialized,
        apiInfo: event_edit_Api,
        validationSchema: undefined,
    })
}

export default EventSettingSpeakers
